/**
 * A select that the page actually controls.
 *
 * WHY NOT A NATIVE <select>
 * The closed control can be styled, but the OPTION LIST is drawn by the operating
 * system and ignores page CSS almost entirely. On a dark surface it renders as a pale
 * native box with a native arrow — the one part of the interface that never matched the
 * theme, and the thing that looked most obviously unfinished.
 *
 * The cost of replacing it is accessibility, which a native select gives away free. So
 * this implements the ARIA combobox-with-listbox pattern properly rather than
 * approximately:
 *
 *   role="combobox" + aria-expanded + aria-controls on the trigger
 *   role="listbox"  + role="option" + aria-selected on the list
 *   aria-activedescendant, so a screen reader follows the highlight without focus moving
 *
 * Keyboard, all of it: Enter/Space/ArrowDown/ArrowUp to open, arrows to move, Home/End
 * to jump, Enter to choose, Escape to abandon, Tab to close and move on. Plus typeahead,
 * because a native select has it and losing it is the kind of regression people notice
 * without being able to name.
 */

import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";

export interface SelectOption {
  value: string;
  label: string;
  /** Shown under the label in the list, never in the closed control. */
  description?: string;
  /** Shown right-aligned — a count, a shortcut, a hint. */
  meta?: string;
  disabled?: boolean;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  id?: string;
  ariaLabel?: string;
  disabled?: boolean;
  /** Matches the compact controls in table rows. */
  size?: "default" | "small";
  className?: string;
}

export function Select({
  value,
  onChange,
  options,
  placeholder = "—",
  id,
  ariaLabel,
  disabled,
  size = "default",
  className = "",
}: Props) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [dropUp, setDropUp] = useState(false);

  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const typed = useRef({ text: "", at: 0 });

  const selectedIndex = useMemo(
    () => options.findIndex((o) => o.value === value),
    [options, value],
  );
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  const close = useCallback((refocus = true) => {
    setOpen(false);
    setActive(-1);
    if (refocus) trigger.current?.focus();
  }, []);

  const choose = useCallback(
    (index: number) => {
      const opt = options[index];
      if (!opt || opt.disabled) return;
      onChange(opt.value);
      close();
    },
    [options, onChange, close],
  );

  /** Skip disabled entries, and stop at the ends rather than wrapping — wrapping makes
   *  a long list feel like it never finishes. */
  const step = useCallback(
    (from: number, delta: number) => {
      let i = from;
      for (let n = 0; n < options.length; n++) {
        i += delta;
        if (i < 0 || i >= options.length) return from < 0 ? (delta > 0 ? 0 : options.length - 1) : from;
        if (!options[i]?.disabled) return i;
      }
      return from;
    },
    [options],
  );

  // Close on an outside click, and on scroll: the list is positioned against the
  // trigger, so leaving it open while the page moves would detach it.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) close(false);
    };
    // An ancestor scrolling really should close it: the list is positioned against the
    // trigger, so it would otherwise detach and float. The list's OWN scrolling must not,
    // which is what the containment check is for.
    const onScroll = (e: Event) => {
      if (!list.current?.contains(e.target as Node)) close(false);
    };
    const onResize = () => close(false);
    document.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open, close]);

  // Flip upwards when there is not room below. Measured after layout, before paint, so
  // the list never appears in the wrong place and then move.
  useLayoutEffect(() => {
    if (!open || !trigger.current) return;
    const box = trigger.current.getBoundingClientRect();
    const needed = Math.min(options.length * 40 + 16, 320);
    setDropUp(box.bottom + needed > window.innerHeight && box.top > needed);
    setActive(selectedIndex >= 0 ? selectedIndex : step(-1, 1));
  }, [open, options.length, selectedIndex, step]);

  // Focus the list itself when it opens. aria-activedescendant is what tells a screen
  // reader which row is current, so focus stays on the list rather than hopping between
  // options -- which is the pattern's whole point and what keeps Escape and Tab working.
  useEffect(() => {
    if (open) list.current?.focus();
  }, [open]);

  // Keep the highlighted row in view when arrowing through a long list.
  //
  // Scrolls the list by hand rather than calling scrollIntoView, which walks UP the tree
  // and scrolls any ancestor that can move. Here that ancestor was .table-scroll, whose
  // scroll event reached the close-on-scroll handler below — so opening the list
  // instantly closed it again. Setting scrollTop directly touches nothing else.
  useEffect(() => {
    if (!open || active < 0) return;
    const box = list.current;
    const row = box?.querySelector<HTMLElement>(`[data-index="${active}"]`);
    if (!box || !row) return;
    const top = row.offsetTop;
    const bottom = top + row.offsetHeight;
    if (top < box.scrollTop) box.scrollTop = top;
    else if (bottom > box.scrollTop + box.clientHeight) box.scrollTop = bottom - box.clientHeight;
  }, [open, active]);

  function onTriggerKey(e: React.KeyboardEvent) {
    if (disabled) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
      return;
    }
    // Typing on a closed control jumps to a match without opening, as native does.
    if (e.key.length === 1 && /\S/.test(e.key)) {
      const i = matchTypeahead(e.key);
      if (i >= 0) choose(i);
    }
  }

  function onListKey(e: React.KeyboardEvent) {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActive((i) => step(i, 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActive((i) => step(i, -1));
        break;
      case "Home":
        e.preventDefault();
        setActive(step(-1, 1));
        break;
      case "End":
        e.preventDefault();
        setActive(step(options.length, -1));
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (active >= 0) choose(active);
        break;
      case "Escape":
        e.preventDefault();
        close();
        break;
      case "Tab":
        // Close but let focus continue, rather than trapping it.
        close(false);
        break;
      default:
        if (e.key.length === 1 && /\S/.test(e.key)) {
          const i = matchTypeahead(e.key);
          if (i >= 0) setActive(i);
        }
    }
  }

  /** Accumulates keystrokes for a second, so "ac" finds Accountant rather than
   *  jumping to A then C. */
  function matchTypeahead(key: string): number {
    const now = Date.now();
    typed.current.text = now - typed.current.at > 1000 ? key : typed.current.text + key;
    typed.current.at = now;
    const needle = typed.current.text.toLowerCase();
    return options.findIndex((o) => !o.disabled && o.label.toLowerCase().startsWith(needle));
  }

  return (
    <div className={`sel ${size === "small" ? "sel--sm" : ""} ${className}`} ref={root}>
      <button
        type="button"
        ref={trigger}
        id={id}
        className={`sel__trigger ${open ? "is-open" : ""}`}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        aria-disabled={disabled}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onTriggerKey}
      >
        <span className={`sel__value ${selected ? "" : "is-placeholder"}`}>
          {selected ? selected.label : placeholder}
        </span>
        <svg className="sel__caret" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
          <path
            d="M6 9.5l6 6 6-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <ul
          ref={list}
          id={listId}
          className={`sel__list ${dropUp ? "is-up" : ""}`}
          role="listbox"
          tabIndex={-1}
          aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
          onKeyDown={onListKey}
        >
          {options.map((o, i) => (
            <li
              key={o.value}
              id={`${listId}-${i}`}
              data-index={i}
              role="option"
              aria-selected={o.value === value}
              aria-disabled={o.disabled}
              className={`sel__opt ${i === active ? "is-active" : ""} ${
                o.value === value ? "is-selected" : ""
              } ${o.disabled ? "is-disabled" : ""}`}
              onMouseEnter={() => !o.disabled && setActive(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(i)}
            >
              <span className="sel__opt-main">
                <span className="sel__opt-label">{o.label}</span>
                {o.description && <span className="sel__opt-desc">{o.description}</span>}
              </span>
              {o.meta && <span className="sel__opt-meta">{o.meta}</span>}
              {o.value === value && (
                <svg className="sel__tick" viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
                  <path
                    d="M4.8 12.6l4.6 4.6L19.2 7.4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
